import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface OrganizationContact {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  job_title?: string;
  profile_image?: string;
  organization_id?: string;
  user_id?: string;
  has_user_account: boolean;
}

export const useOrganizationContacts = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["organization-contacts", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get the admin's organization
      const { data: userRole, error: roleError } = await supabase
        .from('user_roles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (roleError || !userRole?.organization_id) {
        console.error("Error fetching user organization:", roleError);
        return [];
      }

      // Fetch contacts that belong to this organization and don't have user accounts yet
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, phone, job_title, profile_image, organization_id, user_id')
        .eq('organization_id', userRole.organization_id)
        .is('user_id', null)
        .not('first_name', 'is', null)
        .not('last_name', 'is', null)
        .not('email', 'is', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching organization contacts:", error);
        return [];
      }

      const contacts: OrganizationContact[] = (data || []).map(profile => ({
        id: profile.id,
        first_name: profile.first_name,
        last_name: profile.last_name,
        email: profile.email,
        phone: profile.phone,
        job_title: profile.job_title,
        profile_image: profile.profile_image,
        organization_id: profile.organization_id,
        user_id: profile.user_id,
        has_user_account: !!profile.user_id,
      }));

      return contacts;
    },
    enabled: !!user?.id,
  });
};
